import React, { useEffect } from 'react';

import { FlowContextProvider, FlowValues, StepWrapper, useQuery, useReturnToPath } from '@betterment/js-runtime';
import { getLegacySearchParam } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import { Helmet } from 'react-helmet';
import { Route, Routes, useMatch, useSearchParams } from 'react-router-dom';

import AcceptInvitation from './AcceptInvitation';
import AccountLimitReached from './AccountLimitReached';
import InvitationAccountClosed from './InvitationAccountClosed';
import InvitationCanceled from './InvitationCanceled';
import InvitationExpired from './InvitationExpired';
import InvitationLoadingError from './InvitationLoadingError';
import PrimaryBadState from './PrimaryBadState';
import Success from './Success';

import BlockedError from '../Checking/components/BlockedError';
import { useCustomerResponse } from '../Checking/components/CustomerResponseProvider';
import SudoRouteComponent from '../Checking/components/SudoRouteComponent';
import { useUser } from '../Checking/contexts/UserProvider';
import { Invitation, InvitationStatus } from '../Checking/data';
import { CloseButton, ModalContent, ModalOverlay } from '../Checking/modal-helpers';
import { isLocked } from '../Checking/models/card';
import { isManualReview } from '../Checking/models/customer';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

export interface InvitationAcceptanceFlowValues {
  invitation: Invitation;
}
export type InvitationAcceptanceValues = FlowValues<InvitationAcceptanceFlowValues>;

const AuthorizedController: React.FC = () => {
  const jointAccountInvitationId = useRequiredParam('jointAccountInvitationId');
  const [searchParams] = useSearchParams();
  const displaySuccessPage = getLegacySearchParam(searchParams, 'display_success_page');

  const {
    data: invitation,
    loading: isInviteLoading,
    errors,
    refetch: reloadInvitation,
  } = useQuery<Invitation>(urlBuilder.showReceivedInvitation(jointAccountInvitationId), {
    skip: !jointAccountInvitationId,
  });
  const match = useMatch('/checking/joint_accounts_invitations/:jointAccountInvitationId/*');
  const { user } = useUser();
  const { customer, refetch: reloadCustomer } = useCustomerResponse();
  const shouldDisplayLoader = isInviteLoading && !invitation;

  const returnToPath = useReturnToPath(urlBuilder.appHome.absolute());
  const handleClose = () => {
    locationAssign(returnToPath);
  };

  useEffect(() => {
    void reloadCustomer();
  }, [reloadCustomer]);

  if (shouldDisplayLoader) {
    return <FullPageLoader />;
  }

  if (errors || !invitation) {
    return <InvitationLoadingError />;
  }

  if (customer && !displaySuccessPage) {
    const targetAccount = customer.accounts?.find(({ id }) => id === invitation.accountId);
    if (targetAccount && invitation.status === InvitationStatus.Complete) {
      locationAssign(urlBuilder.getAppCheckingSummary.absolute({ accountId: invitation.accountId }));
      return null;
    }
    if (customer.accounts?.length === 2 && !targetAccount) {
      return <AccountLimitReached onClose={handleClose} />;
    }
  }

  if (invitation.status === InvitationStatus.AccountClosed) {
    return <InvitationAccountClosed />;
  }

  if (invitation.status === InvitationStatus.Canceled || invitation.status === InvitationStatus.Declined) {
    return <InvitationCanceled />;
  }

  if (invitation.status === InvitationStatus.Expired) {
    return <InvitationExpired expiredDate={invitation.expiresAt} />;
  }

  if (!invitation.isSenderStatusValid) {
    return <PrimaryBadState senderFirstName={invitation.senderFirstName} />;
  }

  const isCardLocked = customer?.card && isLocked(customer.card);
  const isCustomerLocked = customer?.isLocked;
  const isCustomerInManualReview = customer && isManualReview(customer);
  const isAccountGroupLocked = user?.checkingEligibilityStatus === 'account_group_locked';
  const isUserInManualReview = user?.checkingEligibilityStatus === 'manual_review';
  const isSuspended =
    user?.checkingEligibilityStatus === 'suspended' ||
    user?.checkingEligibilityStatus === 'suspended_documents_required';
  const isInBlockedState =
    isCardLocked ||
    isCustomerLocked ||
    isCustomerInManualReview ||
    isAccountGroupLocked ||
    isSuspended ||
    isUserInManualReview;

  return (
    <ModalOverlay>
      <ModalContent aria-label="Joint Checking signup">
        <CloseButton closeHandler={handleClose} />
        <Helmet>
          <title>Joint Checking signup</title>
        </Helmet>
        <FlowContextProvider<InvitationAcceptanceValues>
          defaultStep={isInBlockedState ? 'locked_error' : undefined}
          initialValues={{ invitation }}
          basePath={match!.pathnameBase}
          routePath=""
          returnToPath={returnToPath}
        >
          <Routes>
            <Route
              data-feature="checking_joint_account_management"
              path="/accept"
              element={
                <StepWrapper<InvitationAcceptanceValues> name="accept">
                  {stepProps => <AcceptInvitation reloadInvitation={reloadInvitation} {...stepProps} />}
                </StepWrapper>
              }
            />
            <Route
              data-feature="checking_joint_account_management"
              path="/success"
              element={
                <StepWrapper<InvitationAcceptanceValues> name="success">
                  {stepProps => <Success isOnlyAccount={customer?.accounts?.length === 1} {...stepProps} />}
                </StepWrapper>
              }
            />
            <Route
              data-feature="checking_joint_account_management"
              path="/locked_error"
              element={
                <StepWrapper name="locked_error">
                  {() =>
                    user !== null ? (
                      <BlockedError
                        title="Joint Checking signup"
                        customer={customer}
                        accountId={customer?.card?.spendingAccountId || 0}
                        user={user}
                      />
                    ) : null
                  }
                </StepWrapper>
              }
            />
          </Routes>
        </FlowContextProvider>
      </ModalContent>
    </ModalOverlay>
  );
};

const JointInvitationAcceptanceController: React.FC = () => {
  return (
    <SudoRouteComponent exitTo={urlBuilder.appHome.absolute()}>
      <AuthorizedController />
    </SudoRouteComponent>
  );
};

export default JointInvitationAcceptanceController;
